import React, { useCallback, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { Transaction } from "@common/types/transaction";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "../TransactionSplitV2/CounterpartyCombobox";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsField } from "./TransactionDetailsField";
import styles from "./TransactionDetailsCounterparty.module.scss";

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
}

// Mirrors TransactionDetailsCounterparty, but for the separate `customer`
// relation (billable-expense customer, PRO-10908) rather than the
// counterparty. Reuses the same CounterpartyCombobox the grid's CustomerCell
// already uses so create/search behavior stays identical between the aside
// panel and the table.
export const TransactionDetailsCustomer: React.FC<Props> = ({
    transaction,
    isLocked,
}) => {
    const { onUpdate } = useContext(TransactionDetailsContext);

    const updateCustomer = useMutation({
        mutationFn: async (customer: CounterpartyComboboxValue | undefined) => {
            await onUpdate({ customerId: customer?.id ?? null });
        },
    });

    const handleChange = useCallback(
        (customer: CounterpartyComboboxValue | undefined) => {
            if (!isLocked) {
                updateCustomer.mutate(customer);
            }
        },
        [isLocked, updateCustomer],
    );

    return (
        <TransactionDetailsField
            label="Billable customer"
            value={
                <CounterpartyCombobox
                    selectedCounterparty={transaction.customer ?? undefined}
                    onChange={handleChange}
                    disabled={isLocked}
                    forTransactionEditing
                    placeholder="Set billable customer"
                    searchPlaceholder="Search billable customers..."
                    createButtonText="Create new billable customer"
                    emptyStateText="No matching billable customers"
                    clearSelectionText="No billable customer"
                    className={styles.counterpartyTrigger}
                    hideArrow
                />
            }
        />
    );
};
